<script lang="ts">
  // Svelte / Kit
  import { onMount } from 'svelte';

  // Ui Components
  import ControlButtonsContainer from '$lib/components/ControlButtonsContainer.svelte';
  import { Button } from '$lib/components/ui/button/index.js';
  import * as HoverCard from '$lib/components/ui/hover-card/index.js';
  // RoastLog
  import {
    lastSavedBurnerChart,
    lastSavedRoastChart,
    plotBurner,
    referenceRoastEventDisplay,
    roastQueue,
    selectedDeviceType,
    selectedLocation,
    selectedRoaster,
    starredRoasts,
    startStopShortcutEnabled,
    startStopShortcutKey,
    userProfile
  } from '$lib/PersistedState.svelte';
  import { globalState, newRoastEvents, roastNotes } from '$lib/State.svelte';
  import { burnerChartDataStore, roastChartDataStore } from '$lib/Stores';
  import { buildHeaders, getStarredRoast, roastUrls } from '$lib/api';
  import ConfirmationAlert from '$lib/components/ConfirmationAlert.svelte';
  import {
    BurnerChart,
    DrawEmptyBurnerProfile,
    PlotBurnerProfile
  } from '$lib/components/roast/BurnerChart';
  import {
    DrawEmptyProfile,
    PlotRoastProfile,
    RoastProfileChart
  } from '$lib/components/roast/RoastChart';
  import { prepareNewRoast, type PrepareRoastDeps } from '$lib/roasting/roastPreparation.svelte';
  import { startRecording, stopRecording } from '$lib/roasting/roastRecorder.svelte';
  import {
    RoastQueueStatus,
    type NewRoast,
    type Nullable,
    type StarredRoast,
    type TimeTempPair
  } from '$lib/types/RoastLog';
  import roastWorkerManager from '$lib/roasting/roastUploadManager';
  import type {
    RoastUploadFailedMessage,
    RoastUploadRetryMessage,
    RoastUploadSuccessMessage,
    RoastUploadWorkerMessage
  } from '$lib/roasting/types';
  // Other things
  import logger from '$lib/logger';
  import { toast } from 'svelte-sonner';
  import { mode } from 'mode-watcher';

  // variables
  let chart: Nullable<RoastProfileChart> = null;
  let resizeTimeout: ReturnType<typeof setTimeout>;

  // State
  let burnerChart: Nullable<BurnerChart> = $state(null);
  let abortRoastDialogOpen = $state(false);
  let referenceRoast: Nullable<StarredRoast> = $state(null);
  let recordButtonDisabled = $derived(
    !globalState.deviceConnected || roastQueue.unroastedLength < 1
  );

  $effect(() => {
    const events = $state.snapshot(newRoastEvents.current);
    if (chart) {
      chart.updateEventDots(events);
    }
  });

  $effect(() => {
    const currentMode = mode.current;
    if (chart) {
      const isDarkMode = currentMode === 'dark';
      if (isDarkMode && !chart.isDarkMode) {
        drawChart();
      } else if (!isDarkMode && chart.isDarkMode) {
        drawChart();
      }
    }
  });

  const loadRoast = async (roastId: number): Promise<RoastProfileChart> => {
    // See if we've cached this roast in the starred roasts store, and if yes load from there. If
    // not, grab it from the API and store it for future use.
    referenceRoast = starredRoasts.current[roastId];

    if (!referenceRoast) {
      return await DrawEmptyProfile();
    }
    return await PlotRoastProfile('chart', referenceRoast, referenceRoastEventDisplay.current);
  };

  const loadBurner = async (roastId: number): Promise<Nullable<BurnerChart>> => {
    referenceRoast = starredRoasts.current[roastId];

    // If the starred roasts haven't been initialized, fetch it syncronously here. This should have
    // been loaded when the template was changes from the roast queue.
    if (!referenceRoast) {
      referenceRoast = await getStarredRoast(roastId);
      starredRoasts.current[roastId] = referenceRoast;
    }

    // There isn't an item in the queue, but we're using a device with burner chart, so draw empty.
    if (!referenceRoast && selectedDeviceType.hasBurnerChart) {
      return DrawEmptyBurnerProfile();
    }

    // There is a reference and it has data for ther burner chart. Show it regardless of device.
    if (referenceRoast.profile.burner && referenceRoast.profile.burner.length > 0) {
      return PlotBurnerProfile('burner-chart', referenceRoast);
    }

    // The reference profile doesn't have a burner, but we're connected to a device with burner chart, so draw empty
    if (selectedDeviceType.hasBurnerChart) {
      return DrawEmptyBurnerProfile();
    }

    // We're not connected to a loring and there is no reference.
    return null;
  };

  /**
   * Logic to start recording a new roast
   */
  const recordButtonClicked = () => {
    // Clear chart view and state
    clearChart();

    // reset the first crack data
    globalState.resetFirstCrack();

    // Clear out notes
    roastNotes.current = '';

    // Finally, start the new recorder and make sure we're now in-progress
    startRecording();
    roastQueue.setCurrentStatus(RoastQueueStatus.InProgress);
  };

  const prepareNewRoastFromState = (id: string): NewRoast => {
    const deps: PrepareRoastDeps = {
      queueItem: roastQueue.currentItem!,
      roastNotes: roastNotes.current,
      userProfile: userProfile.current,
      recordedTemps: globalState.recordedTemps,
      burner: $state.snapshot(globalState.recordedBurner) as TimeTempPair[],
      recordedTimes: globalState.recordedTimes,
      roastEvents: $state.snapshot(newRoastEvents.current),
      deviceType: selectedDeviceType.value,
      selectedRoaster: {
        roasterId: selectedRoaster.roasterId!,
        massScale: selectedRoaster.massScale,
        tcLabels: selectedRoaster.tcLabels
      },
      selectedLocation: selectedLocation.current!
    };

    return prepareNewRoast(id, deps);
  };

  const saveButtonClicked = async () => {
    logger.info('Save button clicked');

    stopRecording();

    // add the UUID of the qItem to the newRoastData so that the worker can radio back which item
    // has been uploaded. This way, the code here knows what row to update.
    const newRoastData = prepareNewRoastFromState(roastQueue.currentItem!.id);

    logger.info('Save roast init', { tmpRoastId: newRoastData.id });

    // Start the background worker
    roastWorkerManager.saveNewRoast({
      newRoastData,
      url: roastUrls.newRoast,
      headers: buildHeaders()
    });

    // now update the queue
    roastQueue.setCurrentStatus(RoastQueueStatus.Saving);

    // Save the roast chart data so we can reload after a refresh or navigating away.
    lastSavedRoastChart.current = $roastChartDataStore;
    lastSavedBurnerChart.current = $burnerChartDataStore;
  };

  const clearChart = () => {
    // Clear out the last roasting profiles states
    lastSavedRoastChart.current = null;
    lastSavedBurnerChart.current = null;

    // Remove the last roasting profiles from the charts
    chart!.clearChart();
    burnerChart?.clearChart();

    // Initialize both of the charts
    initBurnerChart();
    initChart();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    // Check if the event target is a text input field
    const target = event.target as HTMLElement;
    const isTextInput =
      target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    if (isTextInput || !startStopShortcutEnabled) {
      return;
    }
    if (event.code === startStopShortcutKey.current || event.key === startStopShortcutKey.current) {
      event.preventDefault();
      logger.info('Shortcut triggered');
      if (!globalState.isRecording) {
        recordButtonClicked();
      } else {
        saveButtonClicked();
      }
    }
  };

  function roastUploadCallback(data: RoastUploadWorkerMessage) {
    const { status, id } = data;

    logger.info('Save roast callback init', {
      tmpRoastId: id,
      status,
      roastId: 'roast_id' in data ? data.roast_id : undefined,
      roastName: 'roastName' in data ? data.roastName : undefined
    });

    switch (status) {
      case 'retry': {
        handleUploadRetry(data);
        break;
      }
      case 'failed': {
        handleUploadFailure(data);
        break;
      }
      case 'success': {
        handleUploadSuccess(data);
        break;
      }
    }
  }
  // Subscribe to the upload worker.
  roastWorkerManager.subscribe('roastUpload', roastUploadCallback);

  const handleUploadRetry = (data: RoastUploadRetryMessage) => {
    const { id, retryCount, maxRetries, nextRetryDelay, error } = data;

    const qIndex = roastQueue.getItemIndexById(id);
    if (qIndex === null) {
      logger.warn('Count not find item in queue', { id });
      return;
    }

    const retrySecs = Math.round(nextRetryDelay / 1000);
    const msg = `Scheduling retry ${retryCount} of ${maxRetries} in ${retrySecs} seconds`;
    logger.error(msg, { error });
    toast.warning(`Error uploading. ${msg}`);

    roastQueue.updateItemAt(qIndex, { status: RoastQueueStatus.Retrying });
  };

  const handleUploadFailure = (data: RoastUploadFailedMessage) => {
    const { id, maxRetries, error } = data;
    logger.error(`Could not upload roast after ${maxRetries} retries`, { error });

    const qIndex = roastQueue.getItemIndexById(id);
    if (qIndex !== null) {
      const qItem = roastQueue.at(qIndex);
      toast.error(
        `Failed to upload roast queue item #${qIndex + 1} - ${qItem.roastable!.name}, after ${maxRetries} attempts.`
      );
      roastQueue.updateItemAt(qIndex, { status: RoastQueueStatus.Failed });
    }
  };

  const handleUploadSuccess = (data: RoastUploadSuccessMessage) => {
    const { id, roast_id, roastName } = data;

    // Update the roast q store
    const qIndex = roastQueue.getItemIndexById(id);
    if (qIndex !== null) {
      const qItem = roastQueue.updateItemAt(qIndex, {
        status: RoastQueueStatus.Uploaded,
        roastId: roast_id,
        endingMassDisabled: false
      });

      toast.success(`${roastName} has been succesfull uploaded with roast id ${qItem.roastId}`);
      logger.info('Save roast callback complete', { roastId: qItem.roastId });
    } else {
      logger.error(`Could not find roastq item : ${id}`, { tmpRoastId: id });
    }
  };

  const initChart = async () => {
    const queueItem = roastQueue.currentItem;
    if (queueItem && queueItem.referenceRoastId) {
      chart = await loadRoast(parseInt(queueItem.referenceRoastId));
      if (lastSavedRoastChart.current) {
        chart.update(lastSavedRoastChart.current);
      }
    } else {
      chart = await DrawEmptyProfile();
    }
    return chart;
  };

  const initBurnerChart = async () => {
    if (!plotBurner.current) {
      return;
    }

    const queueItem = roastQueue.currentItem;
    if (queueItem && queueItem.referenceRoastId) {
      burnerChart = await loadBurner(parseInt(queueItem.referenceRoastId));

      if (lastSavedBurnerChart.current && burnerChart) {
        burnerChart.update(lastSavedBurnerChart.current);
      }
    } else if (selectedDeviceType.hasBurnerChart) {
      logger.debug('Drawing empty burner chart');
      burnerChart = DrawEmptyBurnerProfile();
    }

    if (burnerChart === null) {
      const element = document.getElementById('burner-chart');
      if (element) {
        logger.debug('burnerChart is null but element found. Removing.');
        element.remove();
      }
    }
  };

  const handleAbortRoast = () => {
    if (globalState.isRecording) {
      stopRecording();
      if (roastQueue.length > 0) {
        roastQueue.setCurrentStatus(RoastQueueStatus.Queued);
      }
      abortRoastDialogOpen = false;
    }
  };

  const drawChart = async () => {
    initBurnerChart();
    // Use the returned chart here, b/c using the global `chart` results in null since this function
    // takes some type with the variable setting, for some reason.
    const ch = await initChart();
    ch.updateEventDots(newRoastEvents.current);
  };

  const debouncedResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(drawChart, 250);
  };

  roastChartDataStore.subscribe((d) => {
    if (chart) {
      chart.update(d);
    }
  });

  burnerChartDataStore.subscribe((d) => {
    if (burnerChart) {
      burnerChart.update(d);
    }
  });

  onMount(() => {
    logger.debug('onMount executing in main page');

    drawChart();

    window.addEventListener('resize', debouncedResize);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      logger.debug('Unmount executing. Removing event listeners');
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('keydown', handleKeyDown);

      chart = null;

      // Try to guard again an inprogress roast that crashed, or the browser was closed.
      if (
        !globalState.isRecording &&
        roastQueue.currentItem !== null &&
        roastQueue.currentItem.status !== RoastQueueStatus.Queued
      ) {
        logger.info('Resetting last roast queue item because of invalid state', {
          status: roastQueue.currentItem.status
        });
        roastQueue.setCurrentStatus(RoastQueueStatus.Queued);
      }
    };
  });
</script>

<!-- Clear the entire queue dialog -->
<ConfirmationAlert
  title="Abort roast?"
  description="Are you sure you want to abort this roast? All recording data will be discarded."
  confirmMsg="Abort"
  handleOk={handleAbortRoast}
  bind:open={abortRoastDialogOpen}
></ConfirmationAlert>

<ControlButtonsContainer>
  {#if globalState.isRecording}
    <Button disabled={!globalState.isRecording} onclick={saveButtonClicked}>Save</Button>
  {:else}
    <Button disabled={recordButtonDisabled} onclick={recordButtonClicked}>Record</Button>
  {/if}
  <Button disabled={!globalState.isRecording} onclick={() => (abortRoastDialogOpen = true)}
    >Abort</Button
  >
  <Button disabled={lastSavedRoastChart.current === null} onclick={clearChart}>Next</Button>
</ControlButtonsContainer>

<div class="bg-card flex h-[calc(100vh-128px)] flex-col justify-normal rounded-lg p-5 pt-2">
  {#if roastQueue.currentItem?.roastable?.name}
    <div class="flex items-center justify-between gap-5 pb-2">
      <div class="w-1/2 text-2xl font-bold">
        {roastQueue.currentItem.status}:
        <span class="text-xl font-normal">
          {roastQueue.currentItem.roastable.name}
          |
          {roastQueue.currentIndex + 1} of {roastQueue.length}
        </span>
      </div>
      {#if referenceRoast?.notes}
        <HoverCard.Root>
          <HoverCard.Trigger class="w-1/2 truncate text-sm">
            {referenceRoast.notes}
          </HoverCard.Trigger>
          <HoverCard.Content>
            {referenceRoast.notes}
          </HoverCard.Content>
        </HoverCard.Root>
      {/if}
    </div>
  {/if}
  <div id="chart" class="flex-1"></div>
</div>
